import { useAuthContext } from "@/providers/auth-provider";
import { useResourceParams } from "@refinedev/core";
import { createContext, useContext, useEffect, useReducer, useState } from "react";

const initialState = {
  resource: null,
  tenantMode: false,
  tenantName: null,
  resourceState: {},
  eks: {},
};

const reducer = (state, action) => {
  const { resource, type, payload } = action;
  switch (type) {
    case "INIT_RESOURCE_STATE":
      return {
        ...state,
        resourceState: {
          ...state.resourceState,
          [resource]: payload,
        },
      };
    case "SELECT_RESOURCE": {
      let resourceState = state.resourceState[resource];
      if (!resourceState) {
        resourceState = initResourceState();
      }
      resourceState.selectedResources = payload ? [payload] : [];
      return {
        ...state,
        resourceState: {
          ...state.resourceState,
          [resource]: resourceState,
        },
      };
    }
    case "TENANT_MODE": {
      if (action.payload === false) {
        return {
          ...state,
          tenantMode: false,
          tenantName: null,
        };
      } else {
        return {
          ...state,
          tenantMode: true,
          tenantName: action.payload.tenantName,
        };
      }
    }
    case "SET_EKS": {
      return {
        ...state,
        eks: payload,
      };
    }
  }
};

const initResourceState = () => {
  return {
    selectedResources: [],
  };
};

const ResourceRootContext = createContext();
const ResourceContext = createContext();

export const ResourceRootProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  return <ResourceRootContext.Provider value={{ state, dispatch }}>{children}</ResourceRootContext.Provider>;
};

export const useResourceRootContext = () => {
  const context = useContext(ResourceRootContext);
  if (context === undefined) {
    throw new Error("useResourceContext must be used within a ResourceRootProvider");
  }
  return context;
};

export const ResourceProvider = ({ children }) => {
  const context = useContext(ResourceRootContext);
  const { state: rootState, dispatch: rootDispatch } = context;
  const { action, id, formAction, resource } = useResourceParams();
  const resourceName = resource?.name;
  const { user, eks } = useAuthContext();
  const [tenantName, setTenantName] = useState(null);

  const dispatch = (action) => {
    action.resource = resourceName;
    rootDispatch(action);
  };
  const getResourceState = () => {
    return rootState.resourceState[resourceName];
  };
  useEffect(() => {
    if (!resourceName) {
      return;
    }
    const checkState = getResourceState();
    if (!checkState) {
      rootDispatch({
        type: "INIT_RESOURCE_STATE",
        resource: resourceName,
        payload: initResourceState(),
      });
    }
  }, [resourceName]);

  useEffect(() => {
    rootDispatch({ type: "SET_EKS", payload: eks });
  }, [eks]);

  useEffect(() => {
    if (rootState.tenantMode) {
      setTenantName(rootState.tenantName);
    } else {
      setTenantName(user?.tenant);
    }
  }, [rootState.tenantName, rootState.tenantMode, user]);

  const state = getResourceState();
  return (
    <ResourceContext.Provider value={{ getResourceState, dispatch, rootState, rootDispatch, tenantName }}>
      {state && children}
    </ResourceContext.Provider>
  );
};

export const useResourceContext = () => {
  const context = useContext(ResourceContext);
  if (context === undefined) {
    throw new Error("useResourceContext must be used within a ResourceProvider");
  }
  return {
    state: context.getResourceState(),
    dispatch: context.dispatch,
    rootState: context.rootState,
    rootDispatch: context.rootDispatch,
    tenantName: context.tenantName,
  };
};
